/**
 * Evaluation Results API Service
 *
 * Fetches precomputed data from the assistant_eval_results Postgres table
 * via the Rust backend endpoints:
 *   GET /api/v1/evaluation/results         — paginated results
 *   GET /api/v1/evaluation/results/runs    — list of runs
 *   GET /api/v1/evaluation/results/runs/{run_id}/summary — run summary
 */

import { API_BASE, fetchApi, getAuthHeaders } from './apiClient';

export interface EvalSource {
  path: string;
  title: string;
  section: string;
  relevance_score: number;
}

/**
 * Matches the assistant_eval_results Postgres table columns.
 * Numeric fields come as numbers, sources as parsed JSON (JSONB).
 */
export interface EvalResult {
  id: number;
  run_id: string;
  question_id: string;
  question_text: string;
  reference_answer: string | null;
  assistant_answer: string | null;
  api_confidence: number | null;
  api_confidence_level: string | null;
  sources: EvalSource[] | null; // JSONB column — already parsed by backend
  similarity_score: number | null;
  keyword_overlap_score: number | null;
  length_ratio: number | null;
  has_sources: number | null;
  retrieval_hit: boolean | null;
  expected_doc_rank: number | null;
  judge_score: number | null; // 0-100 from the Haiku judge
  judge_verdict: string | null;
  judge_model: string | null;
  created_at: string | null;
}

/** Enriched result with derived fields computed client-side. */
export interface ParsedEvalResult extends EvalResult {
  parsedSources: EvalSource[];
  numSimilarity: number;
  numKeywordOverlap: number;
  numConfidence: number;
  numLengthRatio: number;
  numHasSources: number;
  avgSourceRelevance: number;
  compositeScore: number;
}

export interface RunInfo {
  run_id: string;
  total_questions: number;
  created_at: string | null;
  avg_similarity: number | null;
  avg_keyword_overlap: number | null;
  avg_confidence: number | null;
}

export interface RunSummary {
  run_id: string;
  created_at: string;
  total_questions: number;
  avg_similarity: number;
  avg_keyword_overlap: number;
  avg_confidence: number;
  avg_source_relevance: number;
  composite_score: number;
  letter_grade: string;
  score_distribution: {
    excellent: number; // >= 0.85
    good: number;      // >= 0.70
    fair: number;      // >= 0.50
    poor: number;      // < 0.50
  };
  confidence_distribution: {
    high: number;
    medium: number;
    low: number;
  };
}

/** Paginated response from the backend. */
interface EvalResultsListResponse {
  results: EvalResult[];
  total: number;
  limit: number;
  offset: number;
}

// ─── Scoring Logic ───────────────────────────────────────────────────────────

/**
 * Composite score formula (applied client-side per row).
 * Weights:
 *   - Semantic Similarity: 40%
 *   - Keyword Overlap: 20%
 *   - Source Quality: 20% (combo of has_sources + avg relevance)
 *   - Confidence: 20%
 */
function calculateCompositeScore(result: {
  similarity: number;
  keywordOverlap: number;
  confidence: number;
  avgSourceRelevance: number;
  hasSources: boolean;
}): number {
  const sourceScore = result.hasSources
    ? Math.min(1, result.avgSourceRelevance * 1.3)
    : 0;

  return (
    result.similarity * 0.40 +
    result.keywordOverlap * 0.20 +
    sourceScore * 0.20 +
    result.confidence * 0.20
  );
}

function getLetterGrade(score: number): string {
  if (score >= 0.93) return 'A+';
  if (score >= 0.87) return 'A';
  if (score >= 0.83) return 'A-';
  if (score >= 0.80) return 'B+';
  if (score >= 0.75) return 'B';
  if (score >= 0.70) return 'B-';
  if (score >= 0.65) return 'C+';
  if (score >= 0.60) return 'C';
  if (score >= 0.55) return 'C-';
  if (score >= 0.50) return 'D';
  return 'F';
}

/**
 * Enrich a raw DB row with derived numeric fields and composite score.
 * Handles nulls from the database gracefully.
 */
function parseResult(result: EvalResult): ParsedEvalResult {
  // sources is already a parsed JSON object from JSONB column
  const parsedSources: EvalSource[] = Array.isArray(result.sources)
    ? result.sources
    : [];

  const numSimilarity = result.similarity_score ?? 0;
  const numKeywordOverlap = result.keyword_overlap_score ?? 0;
  const numConfidence = result.api_confidence ?? 0;
  const numLengthRatio = result.length_ratio ?? 0;
  const numHasSources = result.has_sources ?? 0;

  const avgSourceRelevance = parsedSources.length > 0
    ? parsedSources.reduce((sum, s) => sum + (s.relevance_score ?? 0), 0) / parsedSources.length
    : 0;

  const compositeScore = calculateCompositeScore({
    similarity: numSimilarity,
    keywordOverlap: numKeywordOverlap,
    confidence: numConfidence,
    avgSourceRelevance,
    hasSources: numHasSources > 0,
  });

  return {
    ...result,
    parsedSources,
    numSimilarity,
    numKeywordOverlap,
    numConfidence,
    numLengthRatio,
    numHasSources,
    avgSourceRelevance,
    compositeScore,
  };
}

function buildRunSummary(results: ParsedEvalResult[]): RunSummary {
  if (results.length === 0) {
    return {
      run_id: '',
      created_at: '',
      total_questions: 0,
      avg_similarity: 0,
      avg_keyword_overlap: 0,
      avg_confidence: 0,
      avg_source_relevance: 0,
      composite_score: 0,
      letter_grade: 'F',
      score_distribution: { excellent: 0, good: 0, fair: 0, poor: 0 },
      confidence_distribution: { high: 0, medium: 0, low: 0 },
    };
  }

  const total = results.length;
  const avgSim = results.reduce((s, r) => s + r.numSimilarity, 0) / total;
  const avgKw = results.reduce((s, r) => s + r.numKeywordOverlap, 0) / total;
  const avgConf = results.reduce((s, r) => s + r.numConfidence, 0) / total;
  const avgSrcRel = results.reduce((s, r) => s + r.avgSourceRelevance, 0) / total;
  const compositeScore = results.reduce((s, r) => s + r.compositeScore, 0) / total;

  const scoreDist = { excellent: 0, good: 0, fair: 0, poor: 0 };
  const confDist = { high: 0, medium: 0, low: 0 };

  results.forEach(r => {
    if (r.compositeScore >= 0.85) scoreDist.excellent++;
    else if (r.compositeScore >= 0.70) scoreDist.good++;
    else if (r.compositeScore >= 0.50) scoreDist.fair++;
    else scoreDist.poor++;

    if (r.api_confidence_level === 'high') confDist.high++;
    else if (r.api_confidence_level === 'medium') confDist.medium++;
    else confDist.low++;
  });

  return {
    run_id: results[0].run_id,
    created_at: results[0].created_at ?? '',
    total_questions: total,
    avg_similarity: avgSim,
    avg_keyword_overlap: avgKw,
    avg_confidence: avgConf,
    avg_source_relevance: avgSrcRel,
    composite_score: compositeScore,
    letter_grade: getLetterGrade(compositeScore),
    score_distribution: scoreDist,
    confidence_distribution: confDist,
  };
}

// ─── API Client ──────────────────────────────────────────────────────────────

class EvalResultsApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/evaluation`;
  }

  /**
   * Fetch all evaluation results from the assistant_eval_results table.
   * Supports pagination for large datasets (4000+ rows).
   * The backend reads directly from Postgres.
   */
  async getResults(runId?: string, limit = 5000, offset = 0): Promise<{
    results: ParsedEvalResult[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (runId) params.set('run_id', runId);
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());

    const url = `${this.baseUrl}/results?${params}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { headers });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${response.statusText}`);
    }

    const data: EvalResultsListResponse = await response.json();
    return {
      results: data.results.map(parseResult),
      total: data.total,
    };
  }

  /**
   * Fetch list of available evaluation runs from the database.
   */
  async getRuns(): Promise<RunInfo[]> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/results/runs`, { headers });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Build a full RunSummary from parsed results (client-side aggregation).
   */
  getSummary(results: ParsedEvalResult[]): RunSummary {
    return buildRunSummary(results);
  }

  getLetterGrade(score: number): string {
    return getLetterGrade(score);
  }

  // ─── Run Management ──────────────────────────────────────────────────

  /**
   * Get total number of available evaluation questions.
   */
  async getQuestionCount(): Promise<{ total: number; approved: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/results/questions/count`, { headers });
    if (!response.ok) throw new Error(`API returned ${response.status}`);
    const data = await response.json();
    // Backend returns {count, total, approved} (total added for compat after
    // the NaN-range bug — this method previously read a key that didn't exist).
    const total = data.total ?? data.count;
    return {
      total: Number.isFinite(total) ? total : 0,
      approved: Number.isFinite(data.approved) ? data.approved : 0,
    };
  }

  /**
   * Trigger a new evaluation run as a background task.
   */
  async startRun(params: {
    start_index: number;
    end_index?: number | null;
    question_ids?: number[] | null; // backend expects i64 ids
  }): Promise<EvalRunStartResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/results/run`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `API returned ${response.status}`);
    }
    return response.json();
  }

  /**
   * Poll progress of a running evaluation.
   */
  async getRunStatus(runId: string): Promise<EvalRunStatusInfo> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/results/run/${runId}/status`, { headers });
    if (!response.ok) throw new Error(`API returned ${response.status}`);
    return response.json();
  }

  /**
   * Check if there is a currently active run.
   */
  async getActiveRun(): Promise<EvalRunStatusInfo | null> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/results/run/active`, { headers });
    if (!response.ok) throw new Error(`API returned ${response.status}`);
    const data = await response.json();
    return data || null;
  }

  /** Rolling question generation (task #5): Haiku frames questions from
   *  random documents; they arrive unapproved for review. */
  async generateQuestions(params: { doc_count?: number; per_doc?: number }): Promise<{
    sampled_docs: number;
    questions_created: number;
    note: string;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    if (!response.ok) throw new Error(`API returned ${response.status}`);
    return response.json();
  }
}

export interface EvalRunStartResponse {
  run_id: string;
  status: string;
  total: number;
  message: string;
}

export interface EvalRunStatusInfo {
  run_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  total: number;
  completed: number;
  failed: number;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
}

export const evalResultsApi = new EvalResultsApi();
